import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaPrint, FaChevronUp, FaChevronDown } from "react-icons/fa";

const BASE_URL = "https://nissi-india.com";

const SupervisorPaymentReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // dropdown data
  const [ddlNames, setDdlNames] = useState([]);
  const [ddlCodes, setDdlCodes] = useState([]);
  const [ddlEntryNos, setDdlEntryNos] = useState([]);
  const [ddlBranches, setDdlBranches] = useState([]);

  // -------------------------
  // localStorage helper
  // -------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // -------------------------
  // localStorage values
  // -------------------------
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const loginuserid = Number(userId || 0);
  const branchid = Number(branchId || 0);
  const branchtype = branch || "HO";

  // -------------------------
  // common headers
  // -------------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // search state
  const [searchData, setSearchData] = useState({
    fromDate: "",
    toDate: "",
    supervisorName: "",
    supervisorCode: "",
    entryNumber: "",
    branchid: branchid || "",
  });

  // report data
  const [reportData, setReportData] = useState([]);

  // -------------------------
  // Load Dropdowns
  // -------------------------
  useEffect(() => {
    const loadDdls = async () => {
      try {
        setLoading(true);

        const [namesRes, codesRes, entryRes, branchRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/supervisorReport/ddl/supervisors/names`, {
            headers: commonHeaders,
          }),
          axios.get(`${BASE_URL}/api/supervisorReport/ddl/supervisors/codes`, {
            headers: commonHeaders,
          }),
          axios.get(`${BASE_URL}/api/supervisorReport/ddl/entrynos`, {
            headers: commonHeaders,
          }),
          axios.get(`${BASE_URL}/api/supervisorReport/ddl/branches`, {
            headers: commonHeaders,
          }),
        ]);

        setDdlNames(namesRes.data?.data || []);
        setDdlCodes(codesRes.data?.data || []);
        setDdlEntryNos(entryRes.data?.data || []);

        const branches = (branchRes.data?.data || []).map((b) => ({
          value: b.fld_branchid ?? b.branchid ?? b.value,
          label: b.fld_branchname ?? b.branchname ?? b.label,
        }));
        setDdlBranches(branches);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || "Failed to load dropdowns");
      } finally {
        setLoading(false);
      }
    };

    loadDdls();
  }, [commonHeaders]);

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const formatDate = (v) => v || "";

  const formatAmount = (v) => {
    const n = Number(v || 0);
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // -------------------------
  // Search
  // -------------------------
  const handleSearch = async () => {
    if (!searchData.branchid) {
      toast.warn("Please select Branch");
      return;
    }

    if (!searchData.fromDate || !searchData.toDate) {
      toast.warn("From Date and To Date are required");
      return;
    }

    try {
      setLoading(true);
      setHasSearched(false);

      const res = await axios.get(`${BASE_URL}/api/supervisorReport/search`, {
        params: {
          supervisorCode: searchData.supervisorCode || "",
          supervisorName: searchData.supervisorName || "",
          fromDate: searchData.fromDate || "",
          toDate: searchData.toDate || "",
          entryNumber: searchData.entryNumber || "",
          branchid: searchData.branchid,
          loginuserid,
        },
        headers: commonHeaders,
      });

      console.log("response:", res.data);

      const rows = (res.data?.data || []).map((r, idx) => ({
        slNo: idx + 1,
        paymentid: r.fld_paymentid ?? r.paymentid,
        date: r.fld_date ?? r.date ?? r.entrydate,
        supervisorCode: r.fld_supervisorCode ?? r.supervisorCode ?? r.supervisorcode,
        supervisorName: r.fld_supervisorname ?? r.supervisorName ?? r.supervisorname,
        amount: r.fld_amount ?? r.amount,
        amountInWords: r.amountInWords ?? r.fld_amountinwords ?? "",
        branchName: r.fld_branchname ?? r.branchName ?? r.branchname,
        entryno: r.fld_entryno ?? r.entryno,
      }));

      setReportData(rows);
      setHasSearched(true);

      toast.success(`Report generated (${rows.length} record(s))`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Search failed");
      setReportData([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Row Print
  // -------------------------
  const handlePrint = (row) => {
    if (!row?.paymentid) {
      toast.error("paymentid missing in row (SP must return fld_paymentid)");
      return;
    }

    window.open(
      `/PaymentVouchers/SupervisorPaymentReport_Print?paymentid=${row.paymentid}`,
      "_blank"
    );
  };

  // -------------------------
  // Print All
  // -------------------------
  const handleAllPrint = () => {
    if (!searchData.branchid) {
      toast.warn("Please select Branch");
      return;
    }

    if (!searchData.fromDate || !searchData.toDate) {
      toast.warn("From Date and To Date are required");
      return;
    }

    const params = new URLSearchParams({
      fromDate: searchData.fromDate || "",
      toDate: searchData.toDate || "",
      supervisorName: searchData.supervisorName || "",
      supervisorCode: searchData.supervisorCode || "",
      entryNumber: searchData.entryNumber || "",
      branchid: searchData.branchid || "",
    });

    window.open(
      `/PaymentVouchers/SupervisorPaymentReport_AllPrint?${params.toString()}`,
      "_blank"
    );
  };

  const branchDisabled = useMemo(() => branchtype !== "HO", [branchtype]);

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUPERVISOR PAYMENT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Supervisor Payment Report
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={searchData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={searchData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={searchData.branchid}
                    onChange={(e) => handleInputChange("branchid", e.target.value)}
                    disabled={branchDisabled}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    } ${branchDisabled ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <option value="">Select</option>
                    {ddlBranches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Name
                  </label>
                  <select
                    value={searchData.supervisorName}
                    onChange={(e) => handleInputChange("supervisorName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlNames.map((x, idx) => (
                      <option key={x.value ?? idx} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Code
                  </label>
                  <select
                    value={searchData.supervisorCode}
                    onChange={(e) => handleInputChange("supervisorCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlCodes.map((x, idx) => (
                      <option key={x.value ?? idx} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Entry Number
                  </label>
                  <select
                    value={searchData.entryNumber}
                    onChange={(e) => handleInputChange("entryNumber", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlEntryNos.map((x, idx) => (
                      <option key={x.value ?? idx} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center mb-6 gap-6">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-white ${
                    isDark ? `${buttonClass} ${hoverClass}` : `${buttonClass} ${hoverClass}`
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleAllPrint}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-white ${
                    isDark ? `${buttonClass} ${hoverClass}` : `${buttonClass} ${hoverClass}`
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaPrint />
                  Print
                </button>
              </div>
            </div>
          )}

          {hasSearched && (
            <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Sl.No</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Date</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Supervisor Code</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Supervisor Name</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Amount</th>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20">Branch Name</th>
                      <th className="px-3 py-3 text-center font-semibold">Print</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {reportData.length === 0 ? (
                      <tr>
                        <td colSpan={7} className={`px-3 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                          No records found
                        </td>
                      </tr>
                    ) : (
                      reportData.map((r) => (
                        <tr
                          key={r.slNo}
                          className={`transition-colors duration-200 ${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.slNo}
                          </td>
                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {formatDate(r.date)}
                          </td>
                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"}`}>
                            {r.supervisorCode}
                          </td>
                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.supervisorName}
                          </td>
                          <td className={`px-3 py-3 text-right border-r font-mono ${isDark ? "border-gray-700 text-green-400" : "border-gray-200 text-green-600"}`}>
                            {formatAmount(r.amount)}
                          </td>
                          <td className={`px-3 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {r.branchName}
                          </td>
                          <td className={`px-3 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                            <button
                              onClick={() => handlePrint(r)}
                              className={`p-2 rounded-lg shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 ${
                                isDark ? "bg-gray-700 hover:bg-gray-600 text-blue-400" : "bg-gray-100 hover:bg-gray-200 text-blue-600"
                              }`}
                              title="Print this record"
                            >
                              <FaPrint />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default SupervisorPaymentReport;